import type { AxiosError, AxiosInstance } from 'axios';
import { toast } from 'sonner';
import { useReportProblemStore } from './reportProblemStore';

/** Reporting the same failing endpoint on every retry would flood the inbox. */
const OFFER_COOLDOWN_MS = 30_000;

/** Endpoints whose failure is expected and handled by the caller's own UI. */
const IGNORED_PATHS = ['/auth/refresh', '/auth/login', '/issue-reports'];

const lastOfferedAt = new Map<string, number>();

function shouldOffer(key: string, now: number) {
  const previous = lastOfferedAt.get(key);
  if (previous !== undefined && now - previous < OFFER_COOLDOWN_MS) return false;
  lastOfferedAt.set(key, now);
  return true;
}

/**
 * Turns a server error into an offer to report it, pre-filled with the endpoint that
 * failed. Deliberately an offer rather than a silent submission: the user knows what they
 * were trying to do, and that sentence is usually what makes the report actionable.
 */
export function applyServerErrorInterceptor(axiosInstance: AxiosInstance) {
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const status = error.response?.status;
      const url = error.config?.url ?? '';

      const isServerFailure = status === undefined || status >= 500;
      const isIgnored = IGNORED_PATHS.some((path) => url.includes(path));

      if (isServerFailure && !isIgnored && shouldOffer(url, Date.now())) {
        const method = error.config?.method?.toUpperCase() ?? 'GET';
        const label = status ? `${status} on ${method} ${url}` : `No response from ${method} ${url}`;

        toast.error('Something went wrong on the server.', {
          description: label,
          action: {
            label: 'Report',
            onClick: () =>
              useReportProblemStore.getState().open({
                title: label,
                errorStack: error.stack ?? error.message,
              }),
          },
        });
      }

      return Promise.reject(error);
    },
  );
}
